import { useEffect, useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { Loader2, RefreshCw, Trash2, Plus, CheckCircle2, Link2 } from 'lucide-react';
import { api, ApiError } from '../lib/api';
import { PLATFORMS, PLATFORM_META, type Platform } from '../lib/platforms';
import { AccountAvatar } from '../components/AccountAvatar';
import { PendingAccountsModal } from '../components/PendingAccountsModal';

type ConnectedAccount = {
  id: string;
  platform: Platform;
  username: string | null;
  displayName: string | null;
  profilePhotoUrl: string | null;
  status: 'connected' | 'disconnected';
  connectedAt: string;
  lastSyncedAt: string;
};

export function Accounts() {
  const queryClient = useQueryClient();
  const [params, setParams] = useSearchParams();
  const [connectingPlatform, setConnectingPlatform] = useState<Platform | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const syncMutateRef = useRef<(() => void) | null>(null);
  // Flag so the next sync's onSuccess knows to force-open the pending modal.
  // Set by the OAuth callback effect; cleared after the sync resolves.
  const forceOpenPendingRef = useRef(false);
  const [blueskyForm, setBlueskyForm] = useState<{ handle: string; password: string } | null>(null);

  const accountsQuery = useQuery({
    queryKey: ['social', 'accounts'],
    queryFn: () => api<{ accounts: ConnectedAccount[] }>('/api/social/accounts'),
  });

  const pendingQuery = useQuery({
    queryKey: ['social', 'pending'],
    queryFn: () => api<{ accounts: ConnectedAccount[] }>('/api/social/accounts/pending'),
  });
  const [showPending, setShowPending] = useState(false);
  // Open the modal whenever the pending list updates and has anything to
  // show. The `dataUpdatedAt` dep makes this re-fire after every fresh
  // network fetch — important so an OAuth callback that re-uses the same
  // accounts (same array contents, same React Query reference) still
  // triggers an open, and so we don't race with whichever fetch was in
  // flight when the sync ran.
  useEffect(() => {
    const pending = pendingQuery.data?.accounts ?? [];
    if (pending.length > 0) {
      setShowPending(true);
      forceOpenPendingRef.current = false;
    } else if (forceOpenPendingRef.current && pendingQuery.isFetched) {
      // We tried to force-open after an OAuth callback but the server
      // says there's nothing pending — Postforme probably returned the
      // already-confirmed account set. Reset the flag so we don't keep
      // attempting on every re-render.
      forceOpenPendingRef.current = false;
    }
  }, [pendingQuery.data, pendingQuery.dataUpdatedAt, pendingQuery.isFetched]);

  const syncMutation = useMutation({
    mutationFn: () => api<{ accounts: ConnectedAccount[] }>('/api/social/accounts/sync', { method: 'POST' }),
    onSuccess: (data) => {
      queryClient.setQueryData(['social', 'accounts'], data);
      // Just invalidate — the useEffect above watches pendingQuery and
      // opens the modal once the refetch lands. Doing the open there
      // (instead of awaiting fetchQuery here) avoids a race where an
      // already-in-flight pending fetch resolves with stale data.
      queryClient.invalidateQueries({ queryKey: ['social', 'pending'] });
    },
    onError: (err: unknown) => {
      if (err instanceof ApiError) setErrorMsg(err.message);
      else setErrorMsg('No se pudo sincronizar.');
      forceOpenPendingRef.current = false;
    },
  });
  syncMutateRef.current = syncMutation.mutate;

  const disconnectMutation = useMutation({
    mutationFn: (id: string) => api(`/api/social/accounts/${id}`, { method: 'DELETE' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['social', 'accounts'] }),
    onError: (err: unknown) => {
      if (err instanceof ApiError) setErrorMsg(err.message);
      else setErrorMsg('No se pudo desconectar la cuenta.');
    },
  });

  useEffect(() => {
    if (params.get('connected') === '1') {
      setParams(new URLSearchParams(), { replace: true });
      // User just came back from OAuth — make the post-sync handler open
      // the pending modal even if the cached pending list didn't change
      // (re-OAuthing the same business returns the same accounts).
      forceOpenPendingRef.current = true;
      syncMutateRef.current?.();
    }
  }, [params, setParams]);

  async function handleConnect(
    platform: Platform,
    opts: { connectionType?: string; blueskyHandle?: string; blueskyAppPassword?: string } = {},
  ) {
    setErrorMsg(null);
    setConnectingPlatform(platform);
    try {
      const { url } = await api<{ url: string }>('/api/social/accounts/connect', {
        method: 'POST',
        body: JSON.stringify({ platform, ...opts }),
      });
      if (!url) {
        setConnectingPlatform(null);
        setErrorMsg(`No se pudo obtener la URL de conexión para ${PLATFORM_META[platform].label}.`);
        return;
      }
      window.location.href = url;
    } catch (err) {
      setConnectingPlatform(null);
      if (err instanceof ApiError && err.code === 'UPSTREAM_ERROR') {
        setErrorMsg(
          `No se pudo conectar con ${PLATFORM_META[platform].label}. Esta red puede no estar disponible en este momento — inténtalo de nuevo más tarde o contacta con soporte.`,
        );
      } else if (err instanceof ApiError) {
        setErrorMsg(err.message);
      } else {
        setErrorMsg('No se pudo iniciar la conexión.');
      }
    }
  }

  const connected = accountsQuery.data?.accounts ?? [];
  const connectedByPlatform = new Map<Platform, ConnectedAccount[]>();
  for (const a of connected) {
    const arr = connectedByPlatform.get(a.platform) ?? [];
    arr.push(a);
    connectedByPlatform.set(a.platform, arr);
  }

  return (
    <>
      {showPending && <PendingAccountsModal onClose={() => setShowPending(false)} />}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2"><Link2 size={22} className="text-brand-600" /> Cuentas conectadas</h1>
          <p className="mt-2 text-slate-600">Conecta las redes sociales en las que quieres publicar.</p>
        </div>
        <button
          onClick={() => syncMutation.mutate()}
          disabled={syncMutation.isPending}
          className="btn-secondary gap-2"
        >
          <RefreshCw size={16} className={syncMutation.isPending ? 'animate-spin' : ''} />
          Sincronizar
        </button>
      </div>

      {params.get('connected') === '1' && (
        <div className="mt-6 card flex items-center gap-3 border-emerald-200 bg-emerald-50">
          <CheckCircle2 className="text-emerald-600" size={20} />
          <p className="text-sm text-emerald-800">Conexión completada. Sincronizando…</p>
        </div>
      )}

      {errorMsg && (
        <div className="mt-6 card border-red-200 bg-red-50">
          <p className="text-sm text-red-700">{errorMsg}</p>
        </div>
      )}

      <section className="mt-8">
        <h2 className="text-lg font-semibold text-slate-900">Tus cuentas</h2>
        <div className="mt-3 card p-0 overflow-hidden">
          {accountsQuery.isLoading ? (
            <div className="p-6 text-slate-500 flex items-center gap-2">
              <Loader2 size={16} className="animate-spin" /> Cargando…
            </div>
          ) : connected.length === 0 ? (
            <div className="p-6 text-slate-500">
              Aún no tienes ninguna cuenta conectada. Elige una red abajo para empezar.
            </div>
          ) : (
            <ul className="divide-y divide-slate-100">
              {connected.map((a) => {
                const meta = PLATFORM_META[a.platform];
                return (
                  <li key={a.id} className="flex items-center justify-between gap-4 px-5 py-4">
                    <div className="flex items-center gap-3 min-w-0">
                      <AccountAvatar account={a} size={40} />
                      <div className="min-w-0">
                        <div className="font-medium text-slate-900 truncate">
                          {a.displayName ?? a.username ?? '—'}
                        </div>
                        <div className="text-xs text-slate-500">
                          {meta.label}
                          {a.status === 'disconnected' && (
                            <span className="ml-2 text-amber-600 font-medium">desconectada</span>
                          )}
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={() => {
                        if (confirm('¿Desconectar esta cuenta?')) disconnectMutation.mutate(a.id);
                      }}
                      disabled={disconnectMutation.isPending}
                      className="btn-secondary gap-2 text-red-600 hover:bg-red-50"
                    >
                      <Trash2 size={14} />
                      Desconectar
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </section>

      <section className="mt-10">
        <h2 className="text-lg font-semibold text-slate-900">Conectar una red</h2>
        <p className="text-sm text-slate-500 mt-1">Puedes conectar varias cuentas de la misma red.</p>
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {PLATFORMS.map((p) => {
            const meta = PLATFORM_META[p];
            const Icon = meta.icon;
            const isConnecting = connectingPlatform === p;
            const count = connectedByPlatform.get(p)?.length ?? 0;

            if (p === 'instagram') {
              return (
                <div key={p} className="card flex flex-col gap-2">
                  <div className="flex items-center gap-3 min-w-0">
                    <span className="inline-flex h-10 w-10 items-center justify-center rounded-lg text-white shrink-0" style={{ backgroundColor: meta.color }}>
                      <Icon size={20} color="white" />
                    </span>
                    <div className="min-w-0">
                      <div className="font-medium text-slate-900">{meta.label}</div>
                      <div className="text-xs text-slate-500">{count > 0 ? `${count} conectada${count > 1 ? 's' : ''}` : 'Sin conectar'}</div>
                    </div>
                    {isConnecting && <Loader2 size={18} className="animate-spin text-slate-400 ml-auto" />}
                  </div>
                  {!isConnecting && (
                    <div className="flex gap-2">
                      <button onClick={() => handleConnect('instagram', { connectionType: 'instagram' })} className="flex-1 text-xs btn-secondary py-1.5">Personal</button>
                      <button onClick={() => handleConnect('instagram', { connectionType: 'facebook' })} className="flex-1 text-xs btn-secondary py-1.5">Business</button>
                    </div>
                  )}
                </div>
              );
            }

            if (p === 'linkedin') {
              return (
                <div key={p} className="card flex flex-col gap-2">
                  <div className="flex items-center gap-3 min-w-0">
                    <span className="inline-flex h-10 w-10 items-center justify-center rounded-lg text-white shrink-0" style={{ backgroundColor: meta.color }}>
                      <Icon size={20} color="white" />
                    </span>
                    <div className="min-w-0">
                      <div className="font-medium text-slate-900">{meta.label}</div>
                      <div className="text-xs text-slate-500">{count > 0 ? `${count} conectada${count > 1 ? 's' : ''}` : 'Sin conectar'}</div>
                    </div>
                    {isConnecting && <Loader2 size={18} className="animate-spin text-slate-400 ml-auto" />}
                  </div>
                  {!isConnecting && (
                    <button onClick={() => handleConnect('linkedin', { connectionType: 'organization' })} className="w-full text-xs btn-secondary py-1.5">
                      Conectar
                    </button>
                  )}
                </div>
              );
            }

            if (p === 'bluesky') {
              const bskyMeta = PLATFORM_META['bluesky'];
              const BskyIcon = bskyMeta.icon;
              return (
                <div key={p} className="card flex flex-col gap-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <span className="inline-flex h-10 w-10 items-center justify-center rounded-lg text-white shrink-0" style={{ backgroundColor: bskyMeta.color }}>
                      <BskyIcon size={20} color="white" />
                    </span>
                    <div className="min-w-0">
                      <div className="font-medium text-slate-900">{bskyMeta.label}</div>
                      <div className="text-xs text-slate-500">{count > 0 ? `${count} conectada${count > 1 ? 's' : ''}` : 'Sin conectar'}</div>
                    </div>
                    {isConnecting && <Loader2 size={18} className="animate-spin text-slate-400 ml-auto" />}
                  </div>
                  {!isConnecting && blueskyForm === null && (
                    <button onClick={() => setBlueskyForm({ handle: '', password: '' })} className="text-xs btn-secondary py-1.5 w-full">
                      Conectar con App Password
                    </button>
                  )}
                  {!isConnecting && blueskyForm !== null && (
                    <div className="flex flex-col gap-2">
                      <input
                        type="text"
                        placeholder="Handle (p.ej. user.bsky.social)"
                        value={blueskyForm.handle}
                        onChange={(e) => setBlueskyForm((f) => f && { ...f, handle: e.target.value })}
                        className="input text-sm py-1.5"
                      />
                      <input
                        type="password"
                        placeholder="App Password"
                        value={blueskyForm.password}
                        onChange={(e) => setBlueskyForm((f) => f && { ...f, password: e.target.value })}
                        className="input text-sm py-1.5"
                      />
                      <div className="flex gap-2">
                        <button
                          onClick={() => {
                            if (blueskyForm.handle && blueskyForm.password) {
                              handleConnect('bluesky', { blueskyHandle: blueskyForm.handle, blueskyAppPassword: blueskyForm.password });
                              setBlueskyForm(null);
                            }
                          }}
                          disabled={!blueskyForm.handle || !blueskyForm.password}
                          className="flex-1 text-xs btn-accent py-1.5 disabled:opacity-50"
                        >
                          Conectar
                        </button>
                        <button onClick={() => setBlueskyForm(null)} className="text-xs btn-secondary py-1.5 px-3">✕</button>
                      </div>
                    </div>
                  )}
                </div>
              );
            }

            return (
              <button
                key={p}
                onClick={() => handleConnect(p)}
                disabled={isConnecting}
                className="card flex items-center justify-between gap-3 hover:border-slate-300 hover:shadow transition text-left disabled:opacity-60"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <span
                    className="inline-flex h-10 w-10 items-center justify-center rounded-lg text-white shrink-0"
                    style={{ backgroundColor: meta.color }}
                  >
                    <Icon size={20} color="white" />
                  </span>
                  <div className="min-w-0">
                    <div className="font-medium text-slate-900">{meta.label}</div>
                    <div className="text-xs text-slate-500">
                      {count > 0 ? `${count} conectada${count > 1 ? 's' : ''}` : 'Sin conectar'}
                    </div>
                  </div>
                </div>
                {isConnecting ? (
                  <Loader2 size={18} className="animate-spin text-slate-400" />
                ) : (
                  <Plus size={18} className="text-slate-400" />
                )}
              </button>
            );
          })}
        </div>
      </section>
    </>
  );
}
